import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Select,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconSearch, IconX } from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { paginationBase } from "../../../_base/model/_base/BaseTable";
import { repositoryMdm, repositoryPos } from "../../../_base/_const/_constVar";
import { MessageResponse } from "../../../model/MessageResponse";
import { getTblDmCustomerGroupSelect } from "../../../service/getSelectApi";

const ZNSConfiguration = () => {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState(paginationBase);
  
  // Filters
  const [keySearch, setKeySearch] = useState("");
  const [blockZnsStatus, setBlockZnsStatus] = useState<string | null>("all");
  const [selectedGroupToBlock, setSelectedGroupToBlock] = useState<string | null>(null);
  const [customerGroups, setCustomerGroups] = useState<any[]>([]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const groups = await getTblDmCustomerGroupSelect();
        if (groups) {
          setCustomerGroups(groups.map((g: any) => ({ value: g.value?.toString(), label: g.text })));
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchGroups();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    try {
      let url = `/api/v1/TblDmCustomer/get-list-v2?Skip=${pagination.pageIndex * pagination.pageSize}&Take=${pagination.pageSize}`;
      if (keySearch) {
        url += `&KeySearch=${keySearch}`;
      }
      if (blockZnsStatus === "true") {
        url += `&BlockZns=true`;
      } else if (blockZnsStatus === "false") {
        url += `&BlockZns=false`;
      }

      const response = await repositoryMdm.get(url);
      if (response && response.success) {
        setData(response.data || []);
        setRowCount(response.totalCount || 0);
        setRowSelection({});
      }
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Lỗi lấy danh sách khách hàng");
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.pageIndex, pagination.pageSize, blockZnsStatus]);

  const handleBlockSelected = async () => {
    const selectedCodes = Object.keys(rowSelection).map((id) => {
       const row = data.find(item => item.id.toString() === id);
       return row ? row.code : null;
    }).filter(code => code !== null) as string[];

    if (!selectedCodes || selectedCodes.length === 0) {
      NotificationExtension.Warn("Vui lòng chọn khách hàng cần chặn");
      return;
    }
    
    try {
      const queryParams = new URLSearchParams();
      selectedCodes.forEach(code => queryParams.append("CustomerCodes", code));

      const response = await repositoryPos.post<MessageResponse<any>>(
        `/api/v1/TblDmCustomer/inactive?${queryParams.toString()}`,
        {}
      );
      
      if (response && response.success) {
        NotificationExtension.Success("Chặn khách hàng thành công");
        fetchData();
      } else {
        NotificationExtension.Fails("Chặn thất bại");
      }
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Có lỗi xảy ra");
    }
  };

  const handleUnblockSelected = async () => {
    const selectedCodes = Object.keys(rowSelection).map((id) => {
       const row = data.find(item => item.id.toString() === id);
       return row ? row.code : null;
    }).filter(code => code !== null) as string[];

    if (!selectedCodes || selectedCodes.length === 0) {
      NotificationExtension.Warn("Vui lòng chọn khách hàng cần bỏ chặn");
      return;
    }
    
    try {
      const queryParams = new URLSearchParams();
      selectedCodes.forEach(code => queryParams.append("CustomerCodes", code));

      const response = await repositoryPos.post<MessageResponse<any>>(
        `/api/v1/TblDmCustomer/active?${queryParams.toString()}`,
        {}
      );
      
      if (response && response.success) {
        NotificationExtension.Success("Bỏ chặn khách hàng thành công");
        fetchData();
      } else {
        NotificationExtension.Fails("Bỏ chặn thất bại");
      }
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Có lỗi xảy ra");
    }
  };

  const handleBlockByGroup = async () => {
    if (!selectedGroupToBlock) {
      NotificationExtension.Warn("Vui lòng chọn nhóm khách hàng cần chặn");
      return;
    }
    
    try {
      const response = await repositoryPos.post<MessageResponse<any>>(
        `/api/v1/TblDmCustomer/inactive?GroupId=${selectedGroupToBlock}`,
        {}
      );
      
      if (response && response.success) {
        NotificationExtension.Success("Chặn nhóm khách hàng thành công");
        fetchData();
      } else {
        NotificationExtension.Fails("Chặn thất bại");
      }
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Có lỗi xảy ra");
    }
  };

  const handleUnblockByGroup = async () => {
    if (!selectedGroupToBlock) {
      NotificationExtension.Warn("Vui lòng chọn nhóm khách hàng cần bỏ chặn");
      return;
    }
    
    try {
      const response = await repositoryPos.post<MessageResponse<any>>(
        `/api/v1/TblDmCustomer/active?GroupId=${selectedGroupToBlock}`,
        {}
      );
      
      if (response && response.success) {
        NotificationExtension.Success("Bỏ chặn nhóm khách hàng thành công");
        fetchData();
      } else {
        NotificationExtension.Fails("Bỏ chặn thất bại");
      }
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Có lỗi xảy ra");
    }
  };

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "code",
        header: "Mã KH",
        enableSorting: false,
      },
      {
        accessorKey: "name",
        header: "Tên khách hàng",
        enableSorting: false,
      },
      {
        accessorKey: "telephoneNumber",
        header: "Số điện thoại",
        enableSorting: false,
      },
      {
        accessorKey: "type",
        header: "Loại KH",
        enableSorting: false,
        Cell: ({ renderedCellValue }: any) => {
          let label = "Khác";
          if (renderedCellValue === 1) label = "Cá nhân";
          else if (renderedCellValue === 2) label = "Doanh nghiệp";
          else if (renderedCellValue === 3) label = "Doanh nghiệp được cấp hạn mức";
          else if (renderedCellValue === 4) label = "Nhân viên HACOM";
          return <Text>{label}</Text>;
        }
      },
      {
        accessorKey: "blockZns",
        header: "Trạng thái ZNS",
        enableSorting: false,
        Cell: ({ row }: any) => {
          const isBlocked = row.original.blockZns;
          return (
             <Badge color={isBlocked ? "red" : "green"}>
                {isBlocked ? "Đang chặn" : "Đang mở"}
             </Badge>
          );
        }
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box style={{ display: "flex", gap: "8px" }}>
             {!row.original.blockZns ? (
               <Tooltip label="Chặn ZNS">
                 <ActionIcon color="red" onClick={() => {
                   setRowSelection({ [row.id]: true });
                   setTimeout(() => handleBlockSelected(), 0);
                 }}>
                   <IconX size={18} />
                 </ActionIcon>
               </Tooltip>
             ) : (
               <Tooltip label="Bỏ chặn ZNS">
                 <ActionIcon color="green" onClick={() => {
                   setRowSelection({ [row.id]: true });
                   setTimeout(() => handleUnblockSelected(), 0);
                 }}>
                   <IconCheck size={18} />
                 </ActionIcon>
               </Tooltip>
             )}
          </Box>
        )
      }
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data,
    enableRowSelection: true,
    getRowId: (row) => row.id?.toString() || Math.random().toString(),
    manualPagination: true,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    rowCount,
    state: {
      isLoading,
      pagination,
      rowSelection,
      showProgressBars: isRefetching,
    },
    renderTopToolbarCustomActions: () => (
      <Group>
        <Button color="red" onClick={handleBlockSelected} disabled={Object.keys(rowSelection).length === 0}>
           Chặn các KH đã chọn
        </Button>
        <Button color="green" onClick={handleUnblockSelected} disabled={Object.keys(rowSelection).length === 0}>
           Bỏ chặn các KH đã chọn
        </Button>
        <Group>
          <Select 
             placeholder="Chọn nhóm KH"
             data={customerGroups}
             value={selectedGroupToBlock}
             onChange={setSelectedGroupToBlock}
             w={250}
             searchable
          />
          <Button color="red" onClick={handleBlockByGroup} disabled={!selectedGroupToBlock}>
             Chặn nhóm này
          </Button>
          <Button color="green" onClick={handleUnblockByGroup} disabled={!selectedGroupToBlock}>
             Bỏ chặn nhóm này
          </Button>
        </Group>
      </Group>
    )
  });

  return (
    <Box p="md">
      <Title order={3} mb="md">Cấu hình chặn ZNS</Title>
      
      <Group mb="md" align="flex-end">
        <TextInput 
           label="Tìm kiếm"
           placeholder="Nhập mã hoặc tên KH"
           value={keySearch}
           onChange={(e) => setKeySearch(e.target.value)}
           onKeyDown={(e) => { if (e.key === 'Enter') fetchData() }}
        />
        <Select 
           label="Trạng thái ZNS"
           data={[
             { value: "all", label: "Tất cả" },
             { value: "true", label: "Đang chặn" },
             { value: "false", label: "Đang mở" }
           ]}
           value={blockZnsStatus}
           onChange={setBlockZnsStatus}
        />
        <Button onClick={fetchData} leftSection={<IconSearch size={16} />}>Tìm kiếm</Button>
      </Group>

      <MantineReactTable table={table} />
    </Box>
  );
};

export default ZNSConfiguration;
